/**
 * Admin — Vibe git projects
 *
 * The operator lever for `VIBE_FORGEJO_REPOSITORY_MISSING`: force ONE git
 * project back to PENDING so the tenant's agent re-materializes its repository.
 * And the census of projects that lever must still visit before every build
 * can clone with a per-project read-only credential.
 *
 * 🔴 DELIBERATELY NOT THE TENANT ENDPOINT, AND THE SPLIT IS STRUCTURAL.
 * `POST /api/vibe/git-projects/:id/reprovision` accepts a FAILED project and
 * nothing else — its re-arm adapter hardcodes `status: FAILED` into the WHERE,
 * so the refusal lives in the database, not in a use-case check. A tenant has
 * no business forcing a healthy-looking (READY) project back to PENDING, so
 * that endpoint stays exactly as it is. This surface is backed by a SEPARATE
 * port whose adapter's WHERE names `FAILED` and `READY`, and the difference
 * between those two WHERE clauses IS the privilege boundary.
 *
 * Cross-org by design, like every admin Vibe surface: `AdminPermissionGuard`
 * enforces PBAC (`admin:vibe-git-project`) and `AdminAuditInterceptor` records
 * the operator. There is no JWT org scope here, which is why the body names its
 * `organizationId` explicitly — the use case resolves the project within that
 * org, so a mismatch is a 404 rather than a cross-tenant write. The Issues page
 * sends it off the issue row's own `organizationId` field, never off
 * `entityId`, which for this code is the git project's id.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { VibeGitProjectStatusReadSchema } from "../vibe/schemas/status-read.schemas";

/**
 * `organizationId` is required and is not decoration — see the file header.
 * `reason` mirrors every other Vibe operator lever's: recorded in the admin
 * audit log, and defaulted by the caller to the issue code it was clicked from.
 */
export const AdminVibeGitProjectReprovisionBodySchema = z
  .object({
    organizationId: z.string().min(1),
    gitProjectId: z.string().uuid(),
    reason: z.string().trim().min(1).max(500)
  })
  .strict();

/**
 * What the press actually did.
 *
 * `previousStatus` is the field that makes the row honest afterwards: FAILED
 * and READY are both accepted and they mean different things happened — a
 * retry of a materialization that never worked, versus an operator overruling
 * a row that claims to be serving. A response that reported only "reprovision
 * requested" could not tell them apart in the audit log.
 */
export const AdminVibeGitProjectReprovisionResponseSchema = z.object({
  gitProjectId: z.string().uuid(),
  name: z.string(),
  previousStatus: VibeGitProjectStatusReadSchema,
  /// Always `PENDING` on success. Stated rather than implied so the row records
  /// the transition it made rather than the one the endpoint is named after.
  status: VibeGitProjectStatusReadSchema
});

/**
 * The read-credential census: every live git project, platform-wide or within
 * one org, that a build CANNOT clone yet because it holds no per-project
 * read-only credential.
 *
 * It exists for the backfill. A project materialized before per-project read
 * credentials existed is READY and never re-offered to its agent, and a build
 * of it is refused at dispatch — so each one must be re-armed once (the
 * `Reprovision` lever above) for its agent to mint the credential. This is the
 * list to re-arm, and the same read proves the backfill finished: a project
 * leaves it the moment its credential is stored.
 */
export const AdminVibeGitProjectReadCredentialCensusQuerySchema = z
  .object({
    /// Narrows the census to one org. Absent = every org.
    organizationId: z.string().min(1).optional()
  })
  .strict();

export const AdminVibeGitProjectMissingReadCredentialSchema = z.object({
  organizationId: z.string(),
  gitProjectId: z.string().uuid(),
  name: z.string(),
  /// PENDING means its agent is already converging it: re-arming it is
  /// neither needed nor accepted. READY and FAILED are what the lever takes.
  status: VibeGitProjectStatusReadSchema
});

export const AdminVibeGitProjectReadCredentialCensusResponseSchema = z.object({
  missing: z.array(AdminVibeGitProjectMissingReadCredentialSchema)
});

export const ZAdminVibeGitProject = {
  Reprovision: {
    method: "POST" as const,
    path: "/api/admin/vibe/git-project/reprovision",
    Body: AdminVibeGitProjectReprovisionBodySchema,
    Response: ApiSuccessSchema(AdminVibeGitProjectReprovisionResponseSchema)
  },
  ReadCredentialCensus: {
    method: "GET" as const,
    path: "/api/admin/vibe/git-project/read-credential-census",
    Query: AdminVibeGitProjectReadCredentialCensusQuerySchema,
    Response: ApiSuccessSchema(AdminVibeGitProjectReadCredentialCensusResponseSchema)
  }
} as const;

// ============================================================
// Convenience type exports
// ============================================================

export type AdminVibeGitProjectReprovisionBody = z.infer<
  typeof AdminVibeGitProjectReprovisionBodySchema
>;
export type AdminVibeGitProjectReprovisionResponse = z.infer<
  typeof AdminVibeGitProjectReprovisionResponseSchema
>;
export type AdminVibeGitProjectReadCredentialCensusResponse = z.infer<
  typeof AdminVibeGitProjectReadCredentialCensusResponseSchema
>;
